import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../../shared/components/table/inline-edit-table";
import {
  CustomSelect,
} from "../../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../../shared/components/buttons/custom-button";
import { ITcProductFacilitiesInformation } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { useFormActions } from "../../../../../../../shared/hooks/form-builder.hooks";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import useNotification from "../../../../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../../../../store/transaction-certificates.store";
import { Utills } from "../../../../../../../shared/app-configs/utilit";
import { SearchInput } from "../../../../../../../shared/components/inputfields/form-elements";
import {
  NON_CERTIFIED_FACILITY_TE_ID,
  buildFacilitySearchOptions,
  searchFacilityTeIds,
} from "./facility-te-id-search.utils";

// Long enough that SearchInput's loading spinner is actually perceptible,
// short enough not to feel sluggish for a search that's really just an
// instant local filter over the seller SC's already-fetched facility list.
const MIN_SEARCH_LOADING_MS = 400;

interface Option {
  value: string;
  label: string;
}

interface ProductFacilitiesInfoProps {
  formRef: React.MutableRefObject<any>;
  ProductFacilitiesData: ITcProductFacilitiesInformation[];
  setProductFacilitiesInfoData: React.Dispatch<
    React.SetStateAction<ITcProductFacilitiesInformation[]>
  >;
}

interface TEDetail {
  TE_ID: string;
  account_name: string;
  street?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  state?: string;
}

const ProductFacilitiesInfo: React.FC<ProductFacilitiesInfoProps> = ({
  formRef,
  ProductFacilitiesData,
  setProductFacilitiesInfoData,
}) => {
  const { notify, dismiss } = useNotification();
  const { TE_ID } = useParams();
  const { updateField } = useFormActions("certified-products");
  const { setRestrictPopUpSave, setRawMtrlCodes } = useTransactionCertificateStore();
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [data, setData] = useState<ITcProductFacilitiesInformation[]>(
    Array.isArray(ProductFacilitiesData) ? ProductFacilitiesData : []
  );
  const [facilityCategoryCode, setFacilityCategoryCode] = useState<Option[]>([]);

  // TE-IDs the seller SC actually lists as a facility (Main Facility /
  // Subsequent Site). The search below is restricted to these plus
  // TE-99999999 so a user can no longer pick a facility that doesn't belong
  // to the seller's own SC (86ajw7fuj).
  const [sellerScFacilityTeIds, setSellerScFacilityTeIds] = useState<string[]>([]);
  const notifiedNotFoundRef = useRef<{ query: string | null; snackbarKey: string | number | null }>({
    query: null,
    snackbarKey: null,
  });

  useEffect(() => {
    const tcId = String(TE_ID || "");
    if (!tcId) {
      setSellerScFacilityTeIds([]);
      return;
    }
    let active = true;
    dtrackItService
      .getFacilityTeIds(tcId)
      .then((ids: string[]) => {
        if (active) setSellerScFacilityTeIds(Array.isArray(ids) ? ids : []);
      })
      .catch(() => {
        if (active) setSellerScFacilityTeIds([]);
      });
    return () => {
      active = false;
    };
  }, [TE_ID]);

  const facilitySearchOptions = useMemo(
    () => buildFacilitySearchOptions(sellerScFacilityTeIds),
    [sellerScFacilityTeIds]
  );

  // Validation
  const validationSchema = Yup.object().shape({
    tcProductFacilitiesTeId: Yup.string()
      .required("Product Facilities TE-ID is required")
      .matches(/^TE-\d{8}$/, "Product Facilities TE-ID must use format TE-########"),
    tcProductFacilitiesProcessCode: Yup.mixed().test(
      "required-process-code",
      "Product Facilities Process Code is required",
      (value) => Array.isArray(value) ? value.length > 0 : Boolean(value)
    ),
  });

  const formik = useFormik<ITcProductFacilitiesInformation>({
    initialValues: {
      tcProductFacilitiesTeId: "",
      tcProductFacilitiesProcessCode: [],
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  // Form ref
  useEffect(() => {
    if (formRef) {
      formRef.current = {
        ...formik,
        editingRowIndex,
        hasUnsavedRow: editingRowIndex !== -1, // Track if a row is being edited
      };
      console.log("ProductFacilitiesInfo - formRef updated:", formRef.current); // Debug log
    }
  }, [formRef, formik, editingRowIndex]);

  // Sync prop changes
  useEffect(() => {
    setData(Array.isArray(ProductFacilitiesData) ? ProductFacilitiesData : []);
  }, [ProductFacilitiesData]);

  // Fetch raw materials
  useEffect(() => {
    const getFacilityProcessCategoryCodes = async () => {
      try {
        const result: any = await dtrackItService.getFacilityProcessCategoryCodes();
        const codes = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
        Utills.cl(`rawMaterials ${JSON.stringify(codes)}`);
        setFacilityCategoryCode(codes);
        setRawMtrlCodes(codes);
      } catch (err) {
        Utills.cl(`Error fetching raw material codes: ${err}`);
        notify("Failed to fetch raw material codes", "error");
      }
    };
    getFacilityProcessCategoryCodes();
  }, [setFacilityCategoryCode]);

  // Table handlers
  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    formik.setFieldValue(columnId, value);
    setData(prev => prev.map((row, i) => (i === rowIndex ? { ...row, [columnId]: value } : row)));
  };

  const handleAddNewRow = () => {
    const newRow: ITcProductFacilitiesInformation = {
      tcProductFacilitiesTeId: "",
      tcProductFacilitiesProcessCode: [],
    };
    const newData = [...data, newRow];
    setData(newData);
    setProductFacilitiesInfoData(newData);
    setEditingRowIndex(newData.length - 1);
    setRestrictPopUpSave(true);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = () => {
    const newData = editingRowIndex === data.length - 1 ? data.slice(0, -1) : [...data];
    setData(newData);
    setProductFacilitiesInfoData(newData);
    try {
      updateField("productFacilitiesInfo", newData); // Update global store
    } catch (error) {
      notify("Failed to update data on cancel", "error");
      console.error("Error updating data on cancel:", error);
    }
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
    (formik as any).hasUnsavedRow = false; // Reset unsaved state
  };

  const handleSaveRow = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach(err => notify(err as string, "error"));
      return;
    }
  
    const updatedRow = formik.values;
    const teId = String(updatedRow.tcProductFacilitiesTeId || "").trim();
    const processCodes = Array.isArray(updatedRow.tcProductFacilitiesProcessCode)
      ? updatedRow.tcProductFacilitiesProcessCode.map((code) => String(code).trim()).filter(Boolean)
      : String(updatedRow.tcProductFacilitiesProcessCode || "").trim()
        ? [String(updatedRow.tcProductFacilitiesProcessCode).trim()]
        : [];

    const duplicateTeId = data.some(
      (row, index) =>
        index !== editingRowIndex &&
        String(row.tcProductFacilitiesTeId || "").trim().toUpperCase() === teId.toUpperCase()
    );
    if (duplicateTeId) {
      notify("Product Facilities TE-ID must be unique", "error");
      return;
    }

    // Defensive re-check: the search already restricts selection to the
    // seller SC's facilities plus TE-99999999, but guard against a stale
    // selection surviving a seller SC change while this row was open.
    const isKnownSellerScFacility = sellerScFacilityTeIds.some(
      (id) => id.toUpperCase() === teId.toUpperCase()
    );
    if (teId.toUpperCase() !== NON_CERTIFIED_FACILITY_TE_ID && !isKnownSellerScFacility) {
      notify(
        `${teId || "This TE-ID"} is not listed as a facility on the seller's SC. Only facilities on the seller's SC or ${NON_CERTIFIED_FACILITY_TE_ID} can be used.`,
        "error"
      );
      return;
    }

    if (teId.toUpperCase() === "TE-99999999" &&
        (processCodes.length !== 1 || processCodes[0].toUpperCase() !== "PR9999")) {
      notify("TE-99999999 must use Product Facilities Process Code PR9999", "error");
      return;
    }

    const newData = data.map((row, i) => (i === editingRowIndex ? updatedRow : row));
    setData(newData);
    setProductFacilitiesInfoData(newData);
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
    (formik as any).hasUnsavedRow = false; // Explicitly reset unsaved state
  
    try {
      updateField("productFacilitiesInfo", newData);
      notify("Row saved successfully", "success");
    } catch (error) {
      notify("Failed to save row", "error");
      console.error("Error saving row:", error);
    }
  };

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    formik.setValues(data[rowIndex] || formik.initialValues);
    setRestrictPopUpSave(true);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const newData = data.filter((_, i) => i !== rowIndex);
    setData(newData);
    setProductFacilitiesInfoData(newData);
    try {
      updateField("productFacilitiesInfo", newData);
      notify("Row deleted successfully", "success");
    } catch (error) {
      notify("Failed to delete row", "error");
      console.error("Error deleting row:", error);
    }
  };

  // Searches only the seller SC's own facility TE-IDs (plus TE-99999999)
  // instead of the org-wide TE-ID directory, so an irrelevant TE-ID is never
  // offered as a selectable option (86ajw7fuj). A query that looks like a
  // query but matches nothing in that scoped list surfaces an error -- the
  // same "no results" a live search elsewhere would report -- instead of
  // silently leaving the user to notice the empty dropdown on their own.
  const searchScopedFacilityTeIds = async (query: string): Promise<TEDetail[]> => {
    // The seller SC's facility list is already fetched once (facilitySearchOptions),
    // so filtering it per keystroke is instant -- SearchInput's loading spinner
    // would otherwise flash for a single tick and never actually be visible,
    // unlike the TE-ID searches elsewhere that wait on a real network call each
    // time. Hold the result briefly so the search still *reads* as a search.
    await new Promise((resolve) => setTimeout(resolve, MIN_SEARCH_LOADING_MS));

    const { matches, notFoundQuery } = searchFacilityTeIds(query, facilitySearchOptions);

    // The error toast is persistent (doesn't auto-hide), so only ever keep
    // one on screen: dismiss the previous "not found" toast before raising
    // a new one, rather than stacking one per keystroke while the user types.
    if (notFoundQuery) {
      if (notifiedNotFoundRef.current.query !== notFoundQuery) {
        if (notifiedNotFoundRef.current.snackbarKey != null) {
          dismiss(notifiedNotFoundRef.current.snackbarKey);
        }
        const snackbarKey = notify(
          `No facility matching "${notFoundQuery}" was found on the seller's SC. Only facilities on the seller's SC or ${NON_CERTIFIED_FACILITY_TE_ID} can be used.`,
          "error"
        );
        notifiedNotFoundRef.current = { query: notFoundQuery, snackbarKey };
      }
    } else if (notifiedNotFoundRef.current.snackbarKey != null) {
      dismiss(notifiedNotFoundRef.current.snackbarKey);
      notifiedNotFoundRef.current = { query: null, snackbarKey: null };
    }

    return matches;
  };

  // Table columns
  const columns: EditableColumnDef<ITcProductFacilitiesInformation>[] = [
    {
      accessorKey: "tcProductFacilitiesTeId",
      header: "Product Facilities TE-ID",
      Header: (
        <span>
          <i style={{ color: "red" }}>*</i> Product Facilities TE-ID
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <SearchInput
          pageName="Product Facility Info"
          value={value}
          onChange={(selected: { [key: string]: any } | null) => {
            onChange(selected ? (selected as TEDetail).TE_ID : "");
          }}
          onSearch={(query: string) => searchScopedFacilityTeIds(query)}
          displayKey="TE_ID"
          placeholder="Search Facility TE-ID"
          restrictInputFormat={true}
          fullWidth={true}
          size="small"
        />
      ),
    },
    {
      accessorKey: "tcProductFacilitiesProcessCode",
      header: "Product Facilities Process Code",
      Header: (
        <span>
          <i style={{ color: "red" }}>*</i> Product Facilities Process Code
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          value={value}
          options={facilityCategoryCode}
          enableSearch={true}
          multiple={true}
          onChange={e => onChange(e.target.value)}
          placeholder="Select Facilities Code"
        />
      ),
    },
  ];

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        p: 2.5,
        border: "1px solid rgba(0, 0, 0, 0.12)",
        borderRadius: "8px",
        my: 1,
      }}
    >
      <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
        <StyledButton
          disabled={editingRowIndex !== -1}
          onClick={handleAddNewRow}
          sx={{ mb: 0 }}
        >
          Add Product Facilities Info
        </StyledButton>
      </Box>

      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <EditableTable
          data={data.filter(row => row && Object.keys(row).length > 0)}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
        />
      </Box>
    </Box>
  );
};

export default ProductFacilitiesInfo;
